function getLocaleFromCompanyCurrency(companyCurrency: string): string {
  switch (companyCurrency) {
    case 'AUD':
      return 'en-AU';
    case 'USD':
      return 'en-US';
    case 'EUR':
      return 'en-EU';
    default:
      return 'en-US';
  }
}

type FormatCurrencyOptions = {
  roundTo?: number;
  roundUp?: boolean;
  currencyDisplay?: 'symbol' | 'narrowSymbol' | 'code';
};

/**
 * Resolve the symbol for an ISO currency code (e.g. `GBP` → `£`, `USD` → `$`). Used in
 * places where we only want the symbol, like the start adornment of a money input.
 * Falls back to the ISO code if `Intl` can't produce a symbol part.
 */
export const getCurrencySymbol = (currency: string): string => {
  try {
    const parts = new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
};

/**
 * Format a currency amount for display. Defaults to `narrowSymbol` so non-locale
 * currencies still render as `£`, `€`, `US$` instead of falling back to the ISO code.
 */
export const formatCurrency = (
  amount: number | undefined,
  currency: string | undefined,
  companyCurrency: string = 'AUD',
  options: FormatCurrencyOptions = { roundTo: 2, roundUp: false },
): string => {
  if (amount === null || amount === undefined || !currency) {
    return '';
  }

  const locale = getLocaleFromCompanyCurrency(companyCurrency);
  const roundTo = options.roundTo ?? 2;
  const fractionDigits = currency === 'JPY' ? 0 : roundTo;
  const currencyDisplay = options.currencyDisplay ?? 'narrowSymbol';

  let amountToFormat = amount;
  if (options.roundUp) {
    const factor = 10 ** fractionDigits;
    amountToFormat = Math.ceil(amount * factor) / factor;
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amountToFormat);
  } catch {
    return `${amountToFormat.toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })} ${currency}`;
  }
};

/**
 * Format a signed price delta for a fare/card ("-$10" / "+$50" / "$0"), or null when there is no delta. A delta under
 * a dollar is rounding noise from the per-leg ↔ whole-trip conversions, so it's clamped to $0 — a near-zero difference
 * never shows as a misleading "-$1".
 */
export const formatSignedDelta = (
  delta: number | null | undefined,
  currency: string | undefined,
  companyCurrency: string,
): string | null => {
  if (delta === null || delta === undefined) {
    return null;
  }
  // Settle the subtraction to cents first. The deltas arrive straight out of a floating point subtraction,
  // where 3102.86 - 1737.86 is 1365.0000000000002, and rounding that up printed a fare card as "+$1,366"
  // beside its own "$3,103 total" and "$1,738 total".
  const settled = Math.round(delta * 100) / 100;
  const clamped = Math.abs(settled) < 1 ? 0 : settled;
  const sign = clamped < 0 ? '-' : clamped > 0 ? '+' : '';
  return `${sign}${formatCurrency(Math.abs(clamped), currency, companyCurrency, { roundTo: 0, roundUp: true })}`;
};
